import { createElement, type CSSProperties, type SVGProps } from 'react';
import { renderIconChildren } from '@source-components-react/shared/icons';
import { PLACEHOLDER_ICON_NODE, resolveIconAttributeValue } from './icon-attribute-value';

const DEFAULT_SIZE = 24;

const A11Y_ATTR_KEYS = [
  'aria-label',
  'aria-labelledby',
  'aria-describedby',
  'role',
  'title',
] as const;

/**
 * One `<svg>` — nothing else.
 *
 * Geometry comes from the attribute the composer wrote, so resolution is
 * synchronous and SSR emits the icon in the initial HTML. Nothing here reads
 * the icon registry (see `icon-attribute-value.ts`).
 *
 * Decorative by default (`aria-hidden`) unless the caller supplies a genuine
 * accessibility prop, matching Lucide's own Icon behaviour.
 */
export function RIcon({
  componentAttributes,
  styleProps = {},
  className,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  styleProps?: { [key: string]: any };
  className?: string;
}>) {
  // An unset attribute shows the placeholder, matching how every other
  // component in this library renders sensibly with no configuration. A value
  // that is set but unusable is a data error and renders nothing, per the
  // unresolvable-icon rule for published pages.
  const value = componentAttributes?.['icon-name-3-resolutions'];
  const iconNode = value == null ? PLACEHOLDER_ICON_NODE : resolveIconAttributeValue(value);

  if (!iconNode) {
    return null;
  }

  const a11yProps = pickA11yProps(componentAttributes);
  const hasA11yProp = Object.keys(a11yProps).length > 0;

  const svgProps: SVGProps<SVGSVGElement> = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: DEFAULT_SIZE,
    height: DEFAULT_SIZE,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    ...(className !== undefined ? { className } : {}),
    style: styleProps as CSSProperties,
    ...a11yProps,
    ...(hasA11yProp ? {} : { 'aria-hidden': true }),
  };

  return createElement('svg', svgProps, ...renderIconChildren(iconNode));
}

const pickA11yProps = (componentAttributes?: {
  [key: string]: any;
}): Record<string, string> => {
  if (!componentAttributes) return {};

  const a11yProps: Record<string, string> = {};
  for (const key of A11Y_ATTR_KEYS) {
    const value = componentAttributes[key];
    if (typeof value === 'string' && value.length > 0) {
      a11yProps[key] = value;
    }
  }
  return a11yProps;
};
